import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Separator } from '@heroui/react';
import { StepBackButton } from '@/pages/catalogImport/components';
import { countParam } from '@/pages/catalogImport/notices';
import type { CatalogImportBlocker } from '@/pages/catalogImport/store';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ActionBarButton } from './ActionBarButton';
import { ActionBarHelper } from './ActionBarHelper';
import { ActionBarStatus } from './ActionBarStatus';

type ActionBarProps = {
    /** Ready rows the user did not exclude: what Import would write. */
    readyCount: number;
    /** Rows that need attention and are not excluded. */
    needsAttentionCount: number;
    /** Rows the user included since a check left them out, with no answer yet. */
    uncheckedCount: number;
    /** Why Import is disabled; `null` when it is enabled. */
    blocker: CatalogImportBlocker | null;
    onExcludeAll: () => void;
    onImport: () => void;
    /**
     * "Import with suggested fixes", offered while rows need attention: it
     * is then the bar's primary action, after Import, which those rows
     * keep blocked (shown disabled, secondary), and the helper line says
     * why and describes it. `null` or left out when no row needs
     * attention: Import is the primary.
     */
    suggestFixes?: { isDisabled: boolean; onPress: () => void } | null;
    /**
     * The rows an import on its way writes, for "Importing N rows…";
     * `readyCount` when left out (a plain import writes the ready rows).
     */
    importingCount?: number;
    /** Back to Names. */
    onBack: () => void;
    /** Back is shown disabled (while the import runs), never hidden. */
    isBackDisabled?: boolean;
    /**
     * A phone: Back is the arrow alone, and it and the two buttons share one
     * line, the buttons with short labels.
     */
    isNarrow?: boolean;
    /**
     * Why the last import did not go through, shown above the bar so it
     * sticks with it (`ImportFailureAlert`); nothing when it did or before.
     */
    notice?: React.ReactNode;
};

/**
 * The Products step's action bar, sticky at the bottom of the step: Back to
 * Names, what the rows need, "Exclude all that need attention", and
 * **Import — N rows** (N = ready rows the user did not exclude).
 *
 * - **Back** ("← Names", the shared `StepBackButton`) is the bar's first
 *   item, then a vertical `Separator`, then the message. It is disabled
 *   while the import runs (`isBackDisabled`), with nothing added to its
 *   name: the message already says the import is running. A running check
 *   does not hold it.
 * - **The message** says the import is running ("Importing N rows…"), or a
 *   check is (with a spinner), or how many rows need attention, or that none
 *   does. It is a polite live region, so an exclusion's new count is read
 *   out, and so is the import's start.
 * - **While the import runs** an indeterminate progress bar sits above the
 *   helper line, which asks to keep the page open, and every button waits,
 *   Back too.
 * - **Exclude all** waits while the import or a check runs and has nothing
 *   to do when no row needs attention.
 * - **Import** is disabled while anything blocks it (`blocker`), and the
 *   helper line beside it, which also describes the button, says why; when
 *   nothing does, it says that nothing is saved until the import.
 * - **Import with suggested fixes** (`suggestFixes`, bos-app#221) is the
 *   primary action while rows need attention, waiting while a check or the
 *   import runs. Import stays beside it, blocked as before (#213: shown
 *   disabled, secondary, before the primary), and the helper line, which
 *   describes both, says why Import is blocked and that this action shows
 *   what it changes first and imports what it cannot fix as drafts.
 * - **`notice`** (why the last import did not go through, with Retry) sits
 *   above the card, inside the sticky wrapper, so it stays by the button
 *   that was pressed.
 *
 * The bar's wrapper is sticky with the page's background and the step's
 * bottom padding, so on a short screen the table scrolls under it and the
 * bar stays in view.
 *
 * **On a phone** (`isNarrow`) the message and the helper stack, and the last
 * line stays one row where the labels fit: Back as HeroUI's icon-only
 * button (named "Back to Names", as the step strip's links are), the
 * separator, and the buttons in HeroUI's small size with short labels
 * ("Exclude all", "Import — 1,402", "Import with fixes"), each growing from
 * its own label into what the arrow leaves, so the bar keeps to about a
 * sixth of the screen. Where they do not, the buttons that do not fit wrap
 * onto the next line, growing to its width, instead of being clipped.
 * Exclude all is then described by the message ("9 rows need attention"),
 * which says which rows it means.
 */
export const ActionBar: React.FC<ActionBarProps> = ({
    readyCount,
    needsAttentionCount,
    uncheckedCount,
    blocker,
    onExcludeAll,
    onImport,
    suggestFixes = null,
    importingCount,
    onBack,
    isBackDisabled = false,
    isNarrow = false,
    notice,
}) => {
    const { t } = useTranslation('pages');
    const statusId = React.useId();
    const helperId = React.useId();
    const isImporting = blocker === 'importing';
    const isChecking = blocker === 'checking';
    const isBusy = isImporting || isChecking;
    const needsAttention = needsAttentionCount > 0;
    const importing = importingCount ?? readyCount;
    const importingMessage = t('catalogImport.products.bar.importing', {
        count: importing,
        rows: countParam(importing),
    });
    // Rows need attention, and the suggested fixes answer them: the helper
    // says why Import is blocked, then describes that action.
    const describesSuggestedFixes =
        suggestFixes !== null && blocker === 'needsAttention';
    const importLabel = t(
        isNarrow
            ? 'catalogImport.products.bar.importShort'
            : 'catalogImport.products.bar.import',
        { count: readyCount, rows: countParam(readyCount) },
    );
    // The bar's first item from sm up; on a phone the first of the button
    // row, which stays one row with the arrow alone.
    const back = (
        <>
            <StepBackButton
                appearance={isNarrow ? 'iconOnly' : 'label'}
                className="shrink-0"
                isDisabled={isBackDisabled}
                onPress={onBack}
                target="names"
            />
            <Separator
                orientation="vertical"
                {...getNodeTestIdProp('catalog-import-action-bar-separator')}
            />
        </>
    );

    return (
        <div
            className="sticky bottom-0 z-10 flex flex-col gap-3 bg-background pb-4 sm:pb-6"
            {...getNodeTestIdProp('catalog-import-action-bar')}
        >
            {notice}
            {/* From sm up the bar is one row while it fits; when it does
                not (Georgian at 1,280 px: the labels take 602 px and left
                the helper 107, eleven lines), the buttons take a second
                line on the right and the helper keeps at least 16rem. */}
            <Card className="flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                {isNarrow ? null : back}
                <ActionBarStatus
                    id={statusId}
                    importingMessage={importingMessage}
                    isChecking={isChecking}
                    isImporting={isImporting}
                    needsAttentionCount={needsAttentionCount}
                />
                <ActionBarHelper
                    blocker={blocker}
                    describesSuggestedFixes={describesSuggestedFixes}
                    id={helperId}
                    importingMessage={importingMessage}
                    uncheckedCount={uncheckedCount}
                />
                {/* On a phone each button grows from its own label (HeroUI's
                    small size): "Import — 1,402" in Georgian needs more than
                    half of what the arrow leaves. Where the labels do not
                    fit beside the arrow ("იმპორტი გასწორებით" at 375 px),
                    the buttons that do not fit wrap onto the next line
                    instead of being clipped. From sm up the row wraps too,
                    right-aligned: with the blocked Import beside Import with
                    suggested fixes the three Georgian labels take about
                    840 px, more than a tablet's bar holds. */}
                <div
                    className="flex flex-wrap items-center gap-2 sm:ml-auto sm:justify-end"
                    {...getNodeTestIdProp('catalog-import-action-bar-buttons')}
                >
                    {isNarrow ? back : null}
                    <ActionBarButton
                        action="excludeAll"
                        describedBy={isNarrow ? statusId : undefined}
                        isDisabled={isBusy || !needsAttention}
                        isNarrow={isNarrow}
                        label={t(
                            isNarrow
                                ? 'catalogImport.products.bar.excludeAllShort'
                                : 'catalogImport.products.bar.excludeAll',
                        )}
                        onPress={onExcludeAll}
                        variant="secondary"
                    />
                    {/* Import: the primary, unless the suggested fixes are
                        offered; then blocked beside them, secondary. */}
                    <ActionBarButton
                        action="import"
                        describedBy={helperId}
                        isDisabled={blocker !== null}
                        isNarrow={isNarrow}
                        label={importLabel}
                        onPress={onImport}
                        variant={
                            suggestFixes === null ? 'primary' : 'secondary'
                        }
                    />
                    {suggestFixes === null ? null : (
                        <ActionBarButton
                            action="suggestedFixes"
                            describedBy={helperId}
                            isDisabled={suggestFixes.isDisabled}
                            isNarrow={isNarrow}
                            label={t(
                                isNarrow
                                    ? 'catalogImport.products.suggestedFixes.actionShort'
                                    : 'catalogImport.products.suggestedFixes.action',
                            )}
                            onPress={suggestFixes.onPress}
                            variant="primary"
                        />
                    )}
                </div>
            </Card>
        </div>
    );
};
